import { Box, useMediaQuery, useTheme } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { AppBar, LocalesMenuButton, useSidebarState, useTranslate, type AppBarProps } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { EnvironmentBadge } from './EnvironmentBadge';
import { findNavEntryForPath } from './navSections';

/**
 * App bar (hybrid-3c.md v3.7 + v3.9 Pattern A): breadcrumb only — the brand
 * wordmark now appears once, as the text lockup at the top of the rail (see
 * CustomMenu), never here. Inset over the content column only: absolutely
 * positioned, `left`/`width` track the rail's current (open/collapsed)
 * width so it never overlaps or gaps against it. The profile menu lives in
 * the rail's bottom card (single place per v2 §5.1), so `userMenu` is
 * disabled and no default toolbar/user-menu slot is rendered here — only
 * the environment pill and the language selector on the right.
 */
export function CustomAppBar(props: AppBarProps) {
  const theme = useTheme();
  const translate = useTranslate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open] = useSidebarState();

  const current = findNavEntryForPath(location.pathname);

  return (
    <AppBar
      {...props}
      color="inherit"
      elevation={0}
      userMenu={false}
      toolbar={null}
      sx={{
        backgroundColor: theme.palette.background.appbar,
        color: theme.palette.text.primary,
        height: '52px',
        minHeight: '52px',
        borderBottom: `1px solid ${theme.palette.divider}`,
        // Desktop: docked over the content column only — absolute, starting
        // at the rail's current right edge (which shifts when the rail
        // collapses) and running to the viewport right. Mobile: full-width
        // fixed over the overlay rail.
        position: isMobile ? 'fixed' : 'absolute',
        top: 0,
        left: isMobile ? 0 : open ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth,
        width: isMobile
          ? '100%'
          : open
            ? `calc(100% - ${theme.custom.menu.openWidth})`
            : `calc(100% - ${theme.custom.menu.closedWidth})`,
        zIndex: isMobile ? 1200 : theme.zIndex.appBar,
        transition: 'left 0.2s ease-in-out, width 0.2s ease-in-out',
        '& .MuiToolbar-root': {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '52px !important',
          height: '52px',
          padding: '0 22px',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        {current && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              color: theme.custom.text.tertiary,
              fontSize: '0.8125rem',
            }}
          >
            <Box component="span">{translate(current.group.labelKey)}</Box>
            <ChevronRightRoundedIcon sx={{ fontSize: '16px' }} />
            <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
              {translate(current.entry.labelKey)}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <EnvironmentBadge />
        <LocalesMenuButton />
      </Box>
    </AppBar>
  );
}
